import { FormEvent, useEffect, useState } from 'react'
import { Form, useNavigate } from 'react-router-dom';
import { Alert, Button, FormControl, IconButton, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import { ITrailer } from '../../types/trailer';
import { ISelectOption } from '../../types/list';
import { ILoadCreateRequest } from '../../types/load';
import { IVendorLight } from '../../types/vendor';
import { ITransporterLight } from '../../types/transporter';

import { useListContext } from '../../contexts/listContext';
import { useUserContext } from '../../contexts/userContext';
import { ErrorProvider } from '../../classes/errorProvider';

import { EmployeeService } from '../../services/employeeService';
import { TrailerService } from '../../services/trailerService';
import { UtilityService } from '../../services/utilityService';
import { VendorService } from '../../services/vendorService';
import { TransporterService } from '../../services/transporterService';
import { LoadService } from '../../services/loadService';
import { CustomerService } from '../../services/customerService';

import '../../styles/loadDetailsForm.scss';

const LoadAddForm = () => {
	const navigate = useNavigate();
	const userContext = useUserContext();
	const listContext = useListContext();

	const [isSaving, setIsSaving] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | undefined>();

	const [customerOptions, setCustomerOptions] = useState<ISelectOption[]>([]);
	const [vendorOptions, setVendorOptions] = useState<ISelectOption[]>([]);
	const [transporterOptions, setTransporterOptions] = useState<ISelectOption[]>([]);
	const [trailers, setTrailers] = useState<ITrailer[]>([]);

	const [isLoadingCustomers, setIsLoadingCustomers] = useState(true);
	const [isLoadingVendors, setIsLoadingVendors] = useState(true);
	const [isLoadingTransporters, setIsLoadingTransporters] = useState(true);
	const [isLoadingTrailers, setIsLoadingTrailers] = useState(true);

	const [customerIdField, setCustomerIdField] = useState<number>();
	const [vendorIdField, setVendorIdField] = useState<number>();
	const [transporterIdField, setTransporterIdField] = useState<number>();
	const [trailerIdField, setTrailerIdField] = useState<number>(-1);
	const [newTrailerNumber, setNewTrailerNumber] = useState<string>("");
	const [newTrailerTypeId, setNewTrailerTypeId] = useState<string>("");
	const [newTrailerLocation, setNewTrailerLocation] = useState<string>("");

	//Help text
	const [customerHelpText, setCustomerHelpText] = useState<string | undefined>("You don't have access to any customers.");
	const [vendorHelpText, setVendorHelpText] = useState<string | undefined>();
	const [transporterHelpText, setTransporterHelpText] = useState<string | undefined>();
	const [addTrailerErrorText, setAddTrailerErrorText] = useState<string | undefined>(undefined);



	useEffect(() => {
		loadCustomerOptions();
	}, [])

	useEffect(() => {
		resetVendors();
		//alert("Reload Vendors");
		if (customerIdField) {
			loadVendorOptions(customerIdField as number);
		}
	}, [customerIdField])

	useEffect(() => {
		resetTransporters();
		if (vendorIdField) {
			loadTransporterOptions(vendorIdField as number);
		}
	}, [vendorIdField])

	useEffect(() => {
		resetTrailers();
		if (transporterIdField) {
			loadTrailers();
		}
	}, [transporterIdField])

	useEffect(() => {
		if (trailerIdField == -1) {
			setNewTrailerNumber("");
			setNewTrailerTypeId("1");
			setNewTrailerLocation("");
		}
	}, [trailerIdField])

	const resetVendors = () => {
		setVendorIdField(-1);
		setVendorOptions([]);
		resetTransporters();
	}

	const resetTransporters = () => {
		setTransporterIdField(-1);
		setTransporterOptions([]);
		resetTrailers();
	}

	const resetTrailers = () => {
		setTrailerIdField(-1);
		setTrailers([]);
	}

	const loadCustomerOptions = async () => {
		setIsLoadingCustomers(true);
		const allCustomers = CustomerService.mapCustomerLightList((await CustomerService.getCustomers())?.data);

		// Update the logged in user's TILS access list
		EmployeeService.getCurrentUser().then((response) => {
			userContext.tilsAccess = response.data.tilsAccess;

			let options: ISelectOption[] = [];
			userContext.tilsAccess?.map((item) => {
				options.push({
					"name": item.customerName,
					"value": item.customerId.toString(),
					"isActive": allCustomers.some(c => +c.id == item.customerId && c.status == "Active"),
				})
			})
			options = options.filter(o => o.isActive);
			options.sort((a, b) => UtilityService.compareStrings(a.name, b.name))

			if (options.length > 0) {
				setCustomerIdField(parseInt(options[0].value));
				setCustomerHelpText(undefined);
			}
			else {
				setCustomerHelpText("You do not have access to any customers.");
			}

			setCustomerOptions(options);
		})
			.catch((error) => {
				console.log('EmployeeService.getCurrentUser: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
			}).finally(() => {
				setIsSaving(false);
				setIsLoadingCustomers(false);
			});
	}

	const loadVendorOptions = async (customerId: number) => {
		setIsLoadingVendors(true);

		VendorService.getVendorsByCustomerId(customerId)
			.then((response) => {
				console.log('VendorService.getVendorsByCustomerId: SUCCESS', response);

				let options: ISelectOption[] = [];
				response.data.map((item: IVendorLight) => {
					options.push({
						"name": item.name,
						"value": item.id,
						"isActive": item.status == "Active",
					})
				})
				options.sort((a, b) => UtilityService.compareStrings(a.name, b.name));

				if (options.length > 0) {
					setVendorIdField(parseInt(options[0].value));
					setVendorHelpText(undefined);
				}
				else {
					setVendorHelpText("The selected customer does not have any vendors.");
				}

				setVendorOptions(options);
				setIsLoadingVendors(false);
			})
			.catch((error) => {
				console.log('VendorService.getVendorsByCustomerId: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
			})
			.finally(() => {
				setIsLoadingVendors(false);
			});
	}

	const loadTransporterOptions = async (vendorId: number) => {
		setIsLoadingTransporters(true);
		TransporterService.getAllByVendor(vendorId)
			.then((response) => {
				console.log('TransporterService.getAllByVendor: SUCCESS', response);

				let options: ISelectOption[] = [];
				response.data.map((item: ITransporterLight) => {
					options.push({
						"name": item.name,
						"value": item.id,
						"isActive": item.status == "Active",
					})
				})
				options.sort((a, b) => UtilityService.compareStrings(a.name, b.name));

				if (options.length > 0) {
					setTransporterIdField(parseInt(options[0].value));
					setTransporterHelpText(undefined);
				}
				else {
					setTransporterHelpText("The selected vendor does not have any transporters.");
				}

				setTransporterOptions(options);
			})
			.catch((error) => {
				console.log('TransporterService.getAllByVendor: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
			})
			.finally(() => {
				setIsLoadingTransporters(false);
				setIsSaving(false);
			});
	}

	const loadTrailers = () => {
		setIsLoadingTrailers(true);
		if (customerIdField && vendorIdField && transporterIdField) {
			TrailerService.getAvailableTrailers(customerIdField, vendorIdField, transporterIdField)
				.then((response) => {
					console.log('TrailerService.getAvailableTrailers: SUCCESS', response);
					let trailers = TrailerService.mapTrailers(response.data);

					trailers.sort((a, b) => UtilityService.compareStrings(a.trailerNumber, b.trailerNumber));

					setTrailerIdField(trailers.length > 0 ? trailers[0].trailerId : -1);

					setTrailers(trailers);
				})
				.catch((error) => {
					console.log('TrailerService.getAvailableTrailers: ERROR', error);
					setErrorMessage(ErrorProvider.getError(error));
				})
				.finally(() => {
					setIsLoadingTrailers(false);
					setIsSaving(false);
				});
		}
	}

	const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
		if (customerIdField && vendorIdField && transporterIdField && trailerIdField) {
			var request: ILoadCreateRequest = {
				customerId: customerIdField,
				vendorId: vendorIdField,
				transporterId: transporterIdField,
				trailerId: trailerIdField,
				newTrailerTypeId: trailerIdField == -1 ? parseInt(newTrailerTypeId) : undefined,
				newTrailerNumber: trailerIdField == -1 ? newTrailerNumber : undefined,
				newTrailerLocation: trailerIdField == -1 ? newTrailerLocation : undefined
			};
			LoadService.addLoad(request)
				.then((response) => {
					console.log('LoadService.addLoad: SUCCESS', response);
					navigate("/tils/" + response.data.id);
				})
				.catch((error) => {
					console.log('LoadService.addLoad: ERROR', error);
					setErrorMessage(ErrorProvider.getError(error));
				})
				.finally(() => {
					setIsSaving(false);
				});
		}
	}

	return (
		<section className="hwi-load-details-form">
			{errorMessage &&
				<Alert className="alert-error"
					severity="error"
					action={<IconButton onClick={() => { setErrorMessage(undefined); }} size='small'>X</IconButton>}>
					{errorMessage}
				</Alert>
			}
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<Form onSubmit={(event) => handleSubmit(event)}>
					<div className="row">
						<InputLabel>Customer *{isLoadingCustomers && " (Loading...)"}</InputLabel>
						{isLoadingCustomers ? (
							<TextField disabled className="customer-field"></TextField>

						) : (
							<>
								<Select
									value={customerIdField == -1 || isLoadingCustomers ? '' : customerIdField}
									className="customer-field"
									required
									onChange={(event) => { setCustomerIdField(event.target.value as number) }}
								>
									{customerOptions.filter(c => c.isActive).map((item, index) => (
										<MenuItem value={item.value} key={index}>{item.name}</MenuItem>
									))}
								</Select>
								{customerHelpText && <p className="help-text">{customerHelpText}</p>}
							</>
						)}
					</div>
					<div className="row">
						<InputLabel>Vendor *{(isLoadingCustomers || isLoadingVendors) && " (Loading...)"}</InputLabel>
						{isLoadingCustomers ? (
							<TextField disabled className="vendor-field"></TextField>
						) : (
							<>
								<Select
									value={vendorIdField == -1 || isLoadingCustomers || isLoadingVendors ? '' : vendorIdField}
									className="vendor-field"
									required
									onChange={(event) => { setVendorIdField(event.target.value as number) }}
								>
									{vendorOptions.filter(v => v.isActive).map((item, index) => (
										<MenuItem value={item.value} key={index}>{item.name}</MenuItem>
									))}
								</Select>
								{vendorHelpText && !(isLoadingCustomers || isLoadingVendors) && <p className="help-text">{vendorHelpText}</p>}
							</>
						)}

					</div>
					<div className="row">
						<InputLabel>Transporter *{(isLoadingCustomers || isLoadingVendors || isLoadingTransporters) && " (Loading...)"}</InputLabel>
						<Select
							value={transporterIdField == -1 || isLoadingCustomers || isLoadingVendors || isLoadingTransporters ? '' : transporterIdField}
							className="transporter-field"
							required
							onChange={(event) => { setTransporterIdField(event.target.value as number) }}
						>
							{transporterOptions.filter(t => t.isActive).map((item, index) => (
								<MenuItem value={item.value} key={index}>{item.name}</MenuItem>
							))}
						</Select>
						{transporterHelpText && !(isLoadingCustomers || isLoadingVendors || isLoadingTransporters) && <p className="help-text">{transporterHelpText}</p>}
					</div>
					<div className="row">
						<InputLabel>Trailer *{(isLoadingCustomers || isLoadingVendors || isLoadingTransporters || isLoadingTrailers) && " (Loading...)"}</InputLabel>
						<Select
							value={isLoadingCustomers || isLoadingVendors || isLoadingTransporters || isLoadingTrailers ? '' : trailerIdField}
							className="trailer-field"
							required
							onChange={(event) => { setTrailerIdField(event.target.value as number) }}
						>
							<MenuItem value={-1}>(New Trailer)</MenuItem>
							{trailers.map((item, index) => (
								<MenuItem value={item.trailerId} key={index}>{item.trailerNumber}</MenuItem>
							))}
						</Select>
					</div>
					<div className="trailer-text-container">
						{addTrailerErrorText && <p className="error-text">* {addTrailerErrorText}</p>}
					</div>
					{!isLoadingTrailers && trailerIdField == -1 &&
						<>
							<div className='row'>
								<InputLabel>Trailer Type *</InputLabel>
								<Select
									value={newTrailerTypeId}
									className='new-trailer-type-field'
									required
									onChange={(event) => { setNewTrailerTypeId(event.target.value as string) }}
								>
									{listContext.trailerTypes.map((item, index) => (
										<MenuItem value={item.value} key={index} onClick={() => setNewTrailerTypeId(item.value.toString())}>{item.name}</MenuItem>
									))}
								</Select>
							</div>
							<div className='row'>
								<InputLabel>Trailer Number *</InputLabel>
								<TextField
									value={newTrailerNumber}
									className='new-trailer-field'
									onChange={(e) => setNewTrailerNumber(e.target.value)}
									onFocus={(e) => { e.target.select(); }}
									placeholder="Enter trailer number"
									inputProps={{ maxLength: 50 }}
									InputLabelProps={{ shrink: true }}
									autoFocus
									fullWidth

									size="small"
								></TextField>
							</div>

						</>
					}
					<div className='row'>
						<InputLabel>Trailer Location</InputLabel>
						<TextField
							value={newTrailerLocation}
							className='new-trailer-location-field'
							onChange={(e) => setNewTrailerLocation(e.target.value)}
							onFocus={(e) => { e.target.select(); }}
							placeholder="Enter trailer location"
							inputProps={{ maxLength: 50 }}
							InputLabelProps={{ shrink: true }}
							fullWidth
							size="small"
						></TextField>
					</div>
					<div className="row controls">
						<Button type="submit" className="save-button" variant="contained" disabled={isSaving}>{isSaving ? "Saving..." : "Save"}</Button>
					</div>
				</Form>
			</LocalizationProvider>
		</section>
	)
}

export default LoadAddForm;